import { useEffect, useState, memo } from 'react';

import { clean } from 'rut.js';

import { useAppDispatch, useAppSelector } from '@/ui/store/helperRedux';
import { changeUserInfoState } from '@/ui/store/slices/showModalsSlice';


import { useScreenSize } from '@/ui/hooks/useScreenSize';
import type { ScreenSize } from '@/ui/hooks/useScreenSize';

import UserController from '@/controllers/UserController';
import IdTypesController from '@/controllers/IdTypesController';

import { IUserResponse } from '@/domain/interfaces/ILoginResponse';
import { IIdTypes } from '@/domain/interfaces/IIdTypes';

import { Col, Container, Row } from 'react-bootstrap';
import '@/ui/containers/userOptions/UserOptionsContainer/style.css';
import { translate } from '@/domain/utils/translate';
import IconList from '@/ui/components/IconList';
import InputList from '@/ui/components/InputList';
import BackClose from '@/ui/components/BackClose';

import Breadcrumbs from '@/ui/components/Breadcrumbs';
import ModalUserInfo from '@/ui/components/user/ModalUserInfo';

import AlertModal from '../modals/AlertModal';
import { IdentificationTypes } from '@/data/libraries/IdentificationTypes';
import { IPhone } from '@/domain/interfaces/IUser';

/** Componente Lista de información de usuario
 * @component
 */
const InfoUserList = (): JSX.Element => {

  /** Hook que contiene las dimensiones de la pantalla y maneja los componentes de acuerdo al resultado obtenido  */
  const { width, maxLargeWidth }: ScreenSize = useScreenSize();
  /** Variable que define si es entorno mobile o desktop */
  const isMobile: boolean = width < maxLargeWidth;

  /** Trae la data del usuario registrado */
  const userLogued = useAppSelector((state) => state.userState);

  /** Controlador que válida el email y token con el endpoint */
  const { getUser } = UserController();

  /** Controlador que obtiene los tipos de identificación */
  const { getIdTypes } = IdTypesController();

  /** Estado inicial de arreglo de tipos */
  const [idTypes, setIdTypes] = useState<IIdTypes[]>([]);

  /** Estado inicial del campo Documento  */
  const [firstName, setFirstName] = useState<string>('');

  /** Estado inicial del campo Documento  */
  const [lastName, setLastName] = useState<string>('');

  /** Estado inicial del campo id oficial  */
  const [idType, setIdType] = useState<string>('');

  /** Estado inicial del campo id oficial  */
  const [document, setDocument] = useState<string>('');

  /** Estado inicial del campo Cumpleaños  */
  const [birthday, setBirthday] = useState<string>('');

  /** Estado inicial del campo Telefono  */
  const [phone, setPhone] = useState<IPhone>({ number: '', country: '' });

  /** Estado inicial del campo ID  */
  const [userId, setId] = useState<number>(0);

  /** Estado para enviar valor al formulario */
  const [valueToShow, setValueToShow] = useState<string>('');

  /** Estado inicial del modal opciones de usuario */
  //const [showUserInfo, setShowUserInfo] = useState<boolean>(false);

  /** Actualización de estado Redux */
  const dispatch = useAppDispatch();

  /** Estado inicial del modal opciones de usuario */
  const showUserInfo: boolean = useAppSelector((state) => state.showModals.showUserInfo);
  /** Evento que muestra y oculta el modal de opciones de usuario */
  const handleShowUserInfo = (): void => {
    dispatch(changeUserInfoState());
  }

  /** Estado inicial del contenido llave del modal */
  const [contentKey, setContentKey] = useState<string>('email');

  /**Estado que maneja el componente en su modo loading */
  const [isLoading, setIsLoading] = useState<boolean>(true);

  /** Estado inicial del modal de alertas */
  const [alert, setAlert] = useState<boolean>(false);


  /** Evento que asigna el key con el que se muestra el contenido del modal */
  const handleContentKey = (key: string): void => {
    setContentKey(key);
  };

  /** Muestra información del cliente */
  const configureUserData = async (): Promise<void> => {
    /** Uso del controlador useUserController con la funcion getUser */
    const userFinded: IUserResponse = await getUser(userLogued.user.email ?? '', userLogued.user.token ?? '');
    setIsLoading(true);
    /** Si el flag de error esta apagado muestra la info */
    if (userFinded.isError === false) {
      setFirstName(userFinded.user!.name.charAt(0).toUpperCase() + userFinded.user!.name.slice(1).toLowerCase());
      if (userFinded.user!.surname && userFinded.user!.surname !== null) {
        setLastName(userFinded.user!.surname.charAt(0).toUpperCase() + userFinded.user!.surname.slice(1).toLowerCase());
      }
      setId(userFinded.user!.id);
      setBirthday(userFinded.user!.birthday);
      setPhone(userFinded.user!.phone);
      setIdType(userFinded.user!.idType);
      if (userFinded.user!.idType === IdentificationTypes.RUT_TYPE.toString()) {
        setDocument(clean(userFinded.user!.identification ?? ''));
      } else {
        setDocument(userFinded.user!.identification);
      }
    }
    setIsLoading(false);
  };

  /** Trae los tipos de identificación del back */
  const fetchIdTypes = async (): Promise<void> => {
    /** Uso del controlador useIdTypesController con la funcion getIdTypes */
    const getIdTypesDropdown: IIdTypes[] = await getIdTypes();
    setIdTypes(getIdTypesDropdown);
  };

  useEffect((): void => {
    /** Trae información del cliente */
    configureUserData();

  }, [userLogued.user.email, userLogued.user.token]);

  useEffect((): void => {
    /** Trae los tipos de identificación */
    fetchIdTypes();
  }, []);

  /** Constantes de texto */
  const VERIFY_ACCOUNT_TEXT: string = translate('user.verify_account_text');
  const ACCOUNT_EMAIL: string = translate('user.account_email');
  const ACCOUNT_NAME: string = translate('user.account_name');
  const ACCOUNT_PHONE: string = translate('user.account_phone');
  const ACCOUNT_OFFICIAL_ID: string = translate('user.account_official_id');
  const UPDATED_TEXT: string = translate('user.updated_text');

  return (
    <>
      {!isLoading ? (
        <Container fluid className={isMobile ? 'p-0' : 'p-0 my-5 g-0'}>
          <Row className='g-0'>
            <Col lg={12} className='d-flex justify-content-center'>
              <Col lg={5} xs={11}>
                {isMobile && (
                  <BackClose classMain='mb-5'>
                    <Breadcrumbs />
                  </BackClose>
                )}
                <Col lg={12} className='d-flex flex-column px-2'>
                  <IconList
                    iconName='ico-info'
                    title={translate('session.account_info')}
                    classMain='fw-bold text-secondary-1 pb-4'
                    classIcon='svg-primary-3 pe-2'
                    classTitle={`${isMobile ? 'h5 ' : 'h3'}`}
                  />
                  <span className={` text-secondary-1   ${isMobile ? 'p-regular-medium' : 'p-large mb-4 paragraph g-0'}`}>{VERIFY_ACCOUNT_TEXT}</span>
                  <Col lg={12} className={isMobile ? 'list-unstyled' : 'list-unstyled pt-5 '}>
                    <InputList
                      title={ACCOUNT_EMAIL}
                      className='text-truncate'
                      text={userLogued.user.email ?? ''}
                      iconName='ico-edit'
                      onClick={() => {
                        setValueToShow(userLogued.user.email ?? '');
                        handleShowUserInfo();
                        handleContentKey('email');
                      }}
                    />
                    <div className='text-capitalize'>
                      <InputList
                        title={ACCOUNT_NAME}
                        className='text-capitalize'
                        text={`${firstName ?? ''}  ${lastName ?? ''}`}
                        iconName='ico-edit'
                        onClick={() => {
                          setValueToShow(`${userLogued.user.firstName ?? ''}|${lastName ?? ''}`);
                          handleShowUserInfo();
                          handleContentKey('name');
                        }}
                      />
                    </div>
                    <InputList
                      title={ACCOUNT_PHONE}
                      text={phone.number ?? ''}
                      iconName='ico-edit'
                      onClick={() => {
                        setValueToShow(JSON.stringify(phone) ?? '');
                        handleShowUserInfo();
                        handleContentKey('phone');
                      }}
                    />
                    <InputList
                      title={ACCOUNT_OFFICIAL_ID}
                      text={document ?? ''}
                      iconName='ico-edit'
                      onClick={() => {
                        setValueToShow(document !== null && document !== '' ? `${document}|${idType}` : '');
                        handleShowUserInfo();
                        handleContentKey('document');
                      }}
                    />
                    {/* <InputList
              title='Cumpleaños'
              text={birthday ?? ''}
              iconName='ico-edit'
              onClick={() => {
                setValueToShow(birthday ?? '');
                handleShowUserInfo();
                handleContentKey('birthdate');
              }}
            />
            <InputList title='Código ID' text={`${userId}`} /> */}
                  </Col>
                </Col>
              </Col>
            </Col>
          </Row>
        </Container>
      ) : (
        <Container fluid className={isMobile ? 'p-0' : 'p-0 my-5 g-0'}>
          <Row className='g-0'>
            <Col lg={12} className='d-flex justify-content-center'>
              <Col lg={5} xs={11}>
                {isMobile && <Col className='gradient-loading mh-50-px rounded mt-4'></Col>}
                <Col lg={12} className='d-flex flex-column px-2'>
                  <Col className='gradient-loading mh-50-px rounded mt-2'></Col>
                  <div className='my-2 h-2-px bg-secondary-4'></div>
                  <Col className='gradient-loading mh-50-px rounded mt-2'></Col>

                  <Col lg={12} className={isMobile ? 'list-unstyled' : 'list-unstyled pt-5 '}>
                    <Col className='gradient-loading mh-50-px rounded mt-2'></Col>
                    <div className='my-2 h-2-px bg-secondary-4'></div>
                    <Col className='gradient-loading mh-50-px rounded mt-2'></Col>
                    <div className='my-2 h-2-px bg-secondary-4'></div>
                    <Col className='gradient-loading mh-50-px rounded mt-2'></Col>
                    <div className='my-2 h-2-px bg-secondary-4'></div>
                    <Col className='gradient-loading mh-50-px rounded mt-2 mb-3'></Col>

                    {/* <InputList
                  title='Cumpleaños'
                  text={birthday ?? ''}
                  iconName='ico-edit'
                  onClick={() => {
                    setValueToShow(birthday ?? '');
                    handleShowUserInfo();
                    handleContentKey('birthdate');
                  }}
                />
                <InputList title='Código ID' text={`${userId}`} /> */}
                  </Col>
                </Col>
              </Col>
            </Col>
          </Row>
        </Container>
      )}

      {showUserInfo && (
        <ModalUserInfo
          showAlert={setAlert}
          showUserInfo={showUserInfo}
          inputValue={valueToShow}
          handleShowUserInfo={handleShowUserInfo}
          contentKey={contentKey}
          idTypes={idTypes}
          updateDataClient={configureUserData}
        />
      )}
      {alert &&
        <AlertModal
          hideAlert={() => setAlert(false)}
          type='success'
          hide={5000}
          content={UPDATED_TEXT}
        />
      }
    </>
  );
};

export default memo(InfoUserList);
